import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from 'auth/auth.api';
import { useAuth } from 'auth/AuthProvider';
import {
  buildProfileCompletionPayload,
  buildProfilePatch,
  firstInvalidField,
  focusField,
  validateProfileValues,
  valuesFromDraft,
  valuesFromUser,
} from 'auth/profileForm';
import { isTokenPersistenceError } from 'auth/sessionClassification';
import {
  clearOtpSession,
  clearPendingOtpCode,
  getDraftProfile,
  getOtpSession,
  getPendingOtpCode,
  setDraftProfile,
} from 'auth/otpSession';
import { invalidateAfterProfileOrLocale } from 'core/api/invalidation';
import { queryKeys } from 'core/api/queryKeys';
import { ApiError } from 'core/errors/ApiError';
import { collectFieldErrors, firstFieldError, humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { destinationAfterLogin, isProfileComplete, paths } from 'core/routing/paths';
import { userApi } from 'features/home/home.api';
import { ProfileFormFields } from 'features/profile/ProfileFormFields';
import { useProfileOptions } from 'features/profile/useProfileOptions';
import { Button } from 'shared/components/Button';
import { ErrorState, Skeleton } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { useCountdown } from 'shared/hooks/useCountdown';
import { useSubmitLock } from 'shared/hooks/useSubmitLock';
import { SafeHtml } from 'shared/utilities/sanitize';

function mapProfileFieldErrors(errors) {
  const keys = [
    'full_name',
    'age_range',
    'education_level',
    'is_employed',
    'work_experience',
    'experience_field',
    'skills',
    'country',
  ];
  const next = {};
  keys.forEach((key) => {
    const message = collectFieldErrors(errors, key)[0] || firstFieldError(errors, key);
    if (message) {
      next[key] = message;
    }
  });
  return next;
}

export function CompleteProfilePage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, status, isAuthenticated, completeLogin, applyUser, retryBootstrap, sessionMessage } = useAuth();
  const { pending, tryAcquire, release, keepLocked } = useSubmitLock();
  const session = getOtpSession();
  const otpCode = getPendingOtpCode();
  const optionsQuery = useProfileOptions({ enabled: status !== 'bootstrapping' });
  const [values, setValues] = useState(() => valuesFromDraft({
    ...getDraftProfile(),
    full_name: user?.name || getDraftProfile()?.full_name || session?.draft_full_name || '',
  }, null));
  const [hydrated, setHydrated] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [openDocument, setOpenDocument] = useState(null);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [otpExpired, setOtpExpired] = useState(false);
  const [retryAfter, setRetryAfter] = useState({ seconds: 0, startedAt: Date.now() });
  const retryIn = useCountdown(retryAfter.seconds, retryAfter.startedAt);
  const throttled = retryIn > 0;

  const legalQuery = useQuery({
    queryKey: queryKeys.legalCurrent,
    queryFn: () => userApi.currentLegal(),
    enabled: !isAuthenticated && !otpExpired,
    retry: false,
  });

  const options = optionsQuery.data || null;

  useEffect(() => {
    if (!options || hydrated) {
      return;
    }
    const draft = getDraftProfile();
    const source = isAuthenticated
      ? valuesFromUser(user, options)
      : valuesFromDraft({
        ...draft,
        full_name: draft?.full_name || user?.name || session?.draft_full_name || '',
      }, options);
    if (!source.country) {
      source.country = options.defaults?.country || '';
    }
    setValues(source);
    setHydrated(true);
  }, [hydrated, isAuthenticated, options, session?.draft_full_name, user]);

  const optionsLoading = optionsQuery.isLoading || optionsQuery.isFetching;
  const optionsFailed = optionsQuery.isError;
  const legalLoading = !isAuthenticated && !otpExpired && (legalQuery.isLoading || legalQuery.isFetching);
  const legalFailed = !isAuthenticated && !otpExpired && legalQuery.isError;
  const blockingLoad = optionsLoading || legalLoading;
  const blockingError = optionsFailed || legalFailed;

  const terms = legalQuery.data?.terms || null;
  const privacy = legalQuery.data?.privacy || null;
  const requiresTerms = Boolean(terms?.id);
  const requiresPrivacy = Boolean(privacy?.id);

  function updateField(name, value) {
    setValues((current) => {
      const next = { ...current, [name]: value };
      if (!isAuthenticated) {
        setDraftProfile(next);
      }
      return next;
    });
    setFieldErrors((current) => {
      if (!current[name]) {
        return current;
      }
      const next = { ...current };
      delete next[name];
      return next;
    });
  }

  function goRequestNewCode() {
    setDraftProfile(values);
    clearPendingOtpCode();
    navigate(paths.verify, {
      replace: true,
      state: { otpExpired: true },
    });
  }

  if (status === 'bootstrapping') {
    return <Skeleton rows={6} />;
  }
  if (status === 'temporarily_unavailable') {
    return (
      <ErrorState
        title={t(sessionMessage || 'auth.temporarilyUnavailable')}
        onRetry={retryBootstrap}
        retryLabel={t('actions.retry')}
        homeAction={false}
      />
    );
  }

  if (isAuthenticated && isProfileComplete(user)) {
    return <Navigate to={destinationAfterLogin(user)} replace />;
  }
  if (!isAuthenticated && !otpExpired && (!session?.phone || otpCode.length !== 6)) {
    return <Navigate to={paths.auth} replace />;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (otpExpired || throttled || blockingLoad || blockingError || !options || !tryAcquire()) {
      return;
    }
    const localErrors = validateProfileValues(values, options, t, { mode: 'onboarding' });
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      setError(t('errors.validation'));
      focusField(firstInvalidField(localErrors));
      release();
      return;
    }
    setError('');
    setFieldErrors({});
    try {
      if (isAuthenticated) {
        const patch = buildProfilePatch(values, user, options);
        const updated = Object.keys(patch).length > 0
          ? await authApi.updateProfile({ ...patch, preferred_locale: locale })
          : user;
        applyUser(updated);
        await invalidateAfterProfileOrLocale(queryClient);
        navigate(destinationAfterLogin(updated), { replace: true });
        return;
      }
      if ((requiresTerms && !acceptTerms) || (requiresPrivacy && !acceptPrivacy)) {
        setError(t('legal.required'));
        release();
        return;
      }
      if ((requiresTerms && !terms?.id) || (requiresPrivacy && !privacy?.id)) {
        setError(t('profileComplete.legalFailed'));
        release();
        return;
      }
      const payload = buildProfileCompletionPayload({
        phone: session.phone,
        code: otpCode,
        values,
        locale,
        acceptTerms,
        acceptPrivacy,
        terms,
        privacy,
        options,
      });
      const data = await authApi.verifyOtp(payload);
      const nextUser = completeLogin(data);
      clearOtpSession();
      navigate(destinationAfterLogin(nextUser, { next_step: data?.next_step }), { replace: true });
    } catch (err) {
      if (isTokenPersistenceError(err)) {
        setError(t('auth.storageUnavailable'));
        release();
      } else if (err instanceof ApiError && err.isUnauthorized()) {
        setDraftProfile(values);
        clearPendingOtpCode();
        setOtpExpired(true);
        setError(t('auth.codeNoLongerValid'));
        keepLocked();
      } else if (err instanceof ApiError && err.isThrottled()) {
        if (err.retryAfterSeconds) {
          setRetryAfter({ seconds: err.retryAfterSeconds, startedAt: Date.now() });
        }
        setError(humanizeApiError(err, t));
        release();
      } else if (err instanceof ApiError && err.status === 503) {
        setError(t('auth.otpDeliveryUnavailable'));
        release();
      } else if (err instanceof ApiError) {
        const mapped = mapProfileFieldErrors(err.errors);
        setFieldErrors(mapped);
        setError(humanizeApiError(err, t));
        focusField(firstInvalidField(mapped));
        release();
      } else {
        setError(t('errors.unexpected'));
        release();
      }
    }
  }

  const openDoc = openDocument === 'terms' ? terms : (openDocument === 'privacy' ? privacy : null);
  const submitDisabled = Boolean(
    pending
    || otpExpired
    || throttled
    || blockingLoad
    || blockingError
    || !options,
  );
  const loadingLabel = optionsLoading
    ? t('profile.optionsLoading')
    : (legalLoading ? t('profileComplete.legalLoading') : t('profileComplete.submit'));
  const retryTitle = optionsFailed ? t('profile.optionsFailed') : t('profileComplete.legalFailed');
  const retryQuery = optionsFailed ? optionsQuery : legalQuery;

  const formBusy = blockingLoad;

  return (
    <div className="auth-page">
      <PageMeta titleKey="meta.profile" />
      <form
        className="card auth-card profile-complete-card"
        onSubmit={handleSubmit}
        aria-label={t('profileComplete.title')}
        aria-busy={formBusy || undefined}
      >
        <h1>{t('profileComplete.title')}</h1>
        <p className="muted">{t('profileComplete.body')}</p>
        {blockingLoad ? <Skeleton rows={6} /> : null}
        {blockingError ? (
          <ErrorState
            title={retryTitle}
            onRetry={() => retryQuery.refetch()}
            retryLabel={t('actions.retry')}
            homeAction={false}
          />
        ) : null}
        {!blockingLoad && !blockingError && options ? (
          <ProfileFormFields
            t={t}
            values={values}
            options={options}
            errors={fieldErrors}
            disabled={otpExpired}
            requireSkills
            onChange={updateField}
          />
        ) : null}
        {!isAuthenticated && !legalLoading && !legalFailed && terms ? (
          <label className="check-row">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(event) => setAcceptTerms(event.target.checked)}
              disabled={otpExpired}
            />
            <span>
              {t('auth.acceptTerms')}
              {' '}
              <button type="button" className="text-link inline-link" onClick={() => setOpenDocument('terms')}>
                {t('legal.read')}
              </button>
              {' · '}
              <Link to={paths.terms}>{t('legal.terms')}</Link>
            </span>
          </label>
        ) : null}
        {!isAuthenticated && !legalLoading && !legalFailed && privacy ? (
          <label className="check-row">
            <input
              type="checkbox"
              checked={acceptPrivacy}
              onChange={(event) => setAcceptPrivacy(event.target.checked)}
              disabled={otpExpired}
            />
            <span>
              {t('auth.acceptPrivacy')}
              {' '}
              <button type="button" className="text-link inline-link" onClick={() => setOpenDocument('privacy')}>
                {t('legal.read')}
              </button>
              {' · '}
              <Link to={paths.privacy}>{t('legal.privacy')}</Link>
            </span>
          </label>
        ) : null}
        {openDoc ? (
          <div className="legal-reader" role="region" aria-label={openDoc.title || t(`legal.${openDocument}`)}>
            <h2>{openDoc.title || t(`legal.${openDocument}`)}</h2>
            <SafeHtml className="legal-preview" html={openDoc.content} />
            <Button type="button" tone="ghost" onClick={() => setOpenDocument(null)}>{t('actions.close')}</Button>
          </div>
        ) : null}
        {error ? <p className="field-error" role="alert">{error}</p> : null}
        {otpExpired ? (
          <Button type="button" block onClick={goRequestNewCode}>{t('auth.requestNewCode')}</Button>
        ) : (
          <Button type="submit" block loading={pending} disabled={submitDisabled}>
            {throttled ? t('errors.throttledWait', { seconds: retryIn }) : loadingLabel}
          </Button>
        )}
      </form>
    </div>
  );
}
